// JobCostTracker.js
// Drop this file into src/screens/
// Then import and add it to Dashboard.js as shown in the instructions

import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../App';
import { mockExpenses, EXPENSE_TYPES } from '../utils/mockData';

const STORAGE_KEY = 'jobtrack_budgets';

function fmt(n) { return '$' + Math.round(n).toLocaleString('en-US'); }
function fmtFull(n) { return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function loadBudgets() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; }
  catch { return {}; }
}

function saveBudgets(b) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(b));
}

export default function JobCostTracker() {
  const { demoMode, showToast, navigate } = useContext(AppContext);
  const [expenses, setExpenses] = useState([]);
  const [budgets, setBudgets] = useState(loadBudgets());
  const [expanded, setExpanded] = useState({});
  const [modal, setModal] = useState(null); // null | { jobName, budget, threshold }
  const [formBudget, setFormBudget] = useState('');
  const [formThreshold, setFormThreshold] = useState('80');
  const [formJobName, setFormJobName] = useState('');

  useEffect(() => {
    setExpenses(demoMode ? mockExpenses : []);
  }, [demoMode]);

  // Group expenses by job
  const jobs = [...new Set(expenses.map(e => e.jobName))].map(jobName => {
    const jobExpenses = expenses.filter(e => e.jobName === jobName);
    const total = jobExpenses.reduce((s, e) => s + Number(e.amount), 0);
    const budget = budgets[jobName]?.budget || 0;
    const threshold = budgets[jobName]?.threshold || 80;
    const pct = budget > 0 ? (total / budget) * 100 : 0;
    const status = pct >= 100 ? 'over' : pct >= threshold ? 'near' : 'on-track';

    const byType = {};
    jobExpenses.forEach(e => {
      byType[e.type] = (byType[e.type] || 0) + Number(e.amount);
    });

    return { jobName, jobExpenses, total, budget, threshold, pct, status, byType };
  }).sort((a, b) => b.total - a.total);

  // Alerts
  const alerts = jobs.filter(j => j.status !== 'on-track' && j.budget > 0);

  function barColor(status) {
    if (status === 'over') return 'var(--accent-red)';
    if (status === 'near') return 'var(--accent-yellow)';
    return 'var(--accent-green)';
  }

  function toggleExpand(jobName) {
    setExpanded(prev => ({ ...prev, [jobName]: !prev[jobName] }));
  }

  function openModal(jobName) {
    setModal(jobName);
    setFormJobName(jobName || '');
    setFormBudget(budgets[jobName]?.budget || '');
    setFormThreshold(budgets[jobName]?.threshold || '80');
  }

  function openNewJob() {
    setModal('__new__');
    setFormJobName('');
    setFormBudget('');
    setFormThreshold('80');
  }

  function saveBudget() {
    const jobName = modal === '__new__' ? formJobName.trim() : modal;
    if (!jobName) { showToast('Please enter a job name', 'error'); return; }
    const updated = {
      ...budgets,
      [jobName]: {
        budget: parseFloat(formBudget) || 0,
        threshold: parseFloat(formThreshold) || 80
      }
    };
    setBudgets(updated);
    saveBudgets(updated);
    setModal(null);
    showToast('Budget saved!');
  }

  const totalSpent = jobs.reduce((s, j) => s + j.total, 0);
  const totalBudget = jobs.reduce((s, j) => s + j.budget, 0);

  return (
    <div>
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, padding: '0 16px 4px' }}>
        {[
          { label: 'Total Spent', val: fmt(totalSpent), color: 'var(--accent-orange)' },
          { label: 'Total Budget', val: fmt(totalBudget), color: 'var(--accent-blue)' },
          { label: 'Remaining', val: fmt(Math.max(0, totalBudget - totalSpent)), color: 'var(--accent-green)' }
        ].map(s => (
          <div key={s.label} className="stat-card" style={{ borderLeft: `3px solid ${s.color}`, padding: 12 }}>
            <div className="stat-card__label">{s.label}</div>
            <div className="stat-card__value" style={{ fontSize: 16, color: s.color }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Alert Banners */}
      {alerts.map(j => (
        <div key={j.jobName} style={{
          margin: '8px 16px 0',
          padding: '10px 14px',
          borderRadius: 10,
          background: j.status === 'over' ? 'rgba(230,58,58,0.1)' : 'rgba(230,194,58,0.1)',
          border: `1px solid ${j.status === 'over' ? 'rgba(230,58,58,0.3)' : 'rgba(230,194,58,0.3)'}`,
          display: 'flex', gap: 10, alignItems: 'flex-start'
        }}>
          <span style={{ fontSize: 18, flexShrink: 0 }}>{j.status === 'over' ? '🔴' : '⚠️'}</span>
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: j.status === 'over' ? 'var(--accent-red)' : 'var(--accent-yellow)', marginBottom: 2 }}>
              {j.status === 'over' ? 'OVER BUDGET' : 'BUDGET ALERT'} — {j.jobName}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
              {j.status === 'over'
                ? `Over budget by ${fmt(j.total - j.budget)}`
                : `${j.pct.toFixed(0)}% of budget used — ${fmt(j.budget - j.total)} remaining`}
            </div>
          </div>
        </div>
      ))}

      {/* Section Label */}
      <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 16 }}>
        Job Cost Tracker
        <button onClick={openNewJob} style={{ fontSize: 11, color: 'var(--accent-orange)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}>
          + New Job
        </button>
      </div>

      {/* Job Cards */}
      {jobs.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state__icon">🏗️</div>
          <div className="empty-state__title">No jobs yet</div>
          <div className="empty-state__text">Add expenses and they'll appear here grouped by job</div>
        </div>
      ) : (
        jobs.map(j => (
          <div key={j.jobName} style={{
            background: 'var(--bg-card)', margin: '0 16px 12px',
            borderRadius: 14, overflow: 'hidden',
            border: `1px solid ${j.status === 'over' ? 'rgba(230,58,58,0.4)' : j.status === 'near' ? 'rgba(230,194,58,0.4)' : 'var(--border)'}`
          }}>
            {/* Job Header */}
            <div style={{ padding: '14px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.jobName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                  {j.jobExpenses.length} expense{j.jobExpenses.length !== 1 ? 's' : ''} · {j.budget > 0 ? `Budget: ${fmt(j.budget)}` : 'No budget set'}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 18, fontWeight: 500, color: barColor(j.status) }}>{fmt(j.total)}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>spent</div>
              </div>
            </div>

            {/* Budget Bar */}
            {j.budget > 0 && (
              <div style={{ padding: '0 16px 10px' }}>
                <div style={{ height: 8, background: 'var(--bg-raised)', borderRadius: 4, overflow: 'hidden', marginBottom: 5 }}>
                  <div style={{ height: '100%', width: `${Math.min(j.pct, 100)}%`, background: barColor(j.status), borderRadius: 4, transition: 'width .5s ease' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-muted)' }}>
                  <span>{j.pct.toFixed(0)}% of budget used</span>
                  <span>{fmt(Math.max(0, j.budget - j.total))} left</span>
                </div>
              </div>
            )}

            {/* Category Chips */}
            <div style={{ display: 'flex', gap: 6, padding: '0 16px 12px', flexWrap: 'wrap' }}>
              {Object.entries(j.byType).map(([type, val]) => {
                const t = EXPENSE_TYPES.find(x => x.id === type) || { icon: '📋', label: type, color: '#666' };
                return (
                  <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--bg-raised)', borderRadius: 20, padding: '4px 10px', fontSize: 11 }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                    <span style={{ color: 'var(--text-secondary)' }}>{t.icon} {t.label}</span>
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{fmt(val)}</span>
                  </div>
                );
              })}
            </div>

            {/* Expand Toggle */}
            <div
              onClick={() => toggleExpand(j.jobName)}
              style={{ borderTop: '1px solid var(--border)', padding: '10px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: 'var(--bg-raised)' }}
            >
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>View {j.jobExpenses.length} expenses</span>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <button
                  onClick={e => { e.stopPropagation(); openModal(j.jobName); }}
                  style={{ fontSize: 11, color: 'var(--accent-orange)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)', fontWeight: 600 }}
                >
                  ✏️ Edit budget
                </button>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', transition: 'transform .2s', display: 'inline-block', transform: expanded[j.jobName] ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
              </div>
            </div>

            {/* Expanded Expense List */}
            {expanded[j.jobName] && (
              <div style={{ borderTop: '1px solid var(--border)' }}>
                {[...j.jobExpenses].sort((a, b) => new Date(b.date) - new Date(a.date)).map((e, i) => {
                  const t = EXPENSE_TYPES.find(x => x.id === e.type) || { icon: '📋', color: '#666', label: e.type };
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', borderBottom: i < j.jobExpenses.length - 1 ? '1px solid var(--border)' : 'none' }}>
                      <div style={{ width: 32, height: 32, borderRadius: 6, background: `${t.color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0 }}>{t.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{e.date} · {t.label}</div>
                      </div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: t.color, flexShrink: 0 }}>{fmtFull(e.amount)}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))
      )}

      {/* Budget Modal */}
      {modal && (
        <div
          onClick={e => { if (e.target === e.currentTarget) setModal(null); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 100 }}
        >
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-bright)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: 24 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, letterSpacing: 1, marginBottom: 16 }}>
              {modal === '__new__' ? 'NEW JOB' : 'EDIT BUDGET'}
            </div>

            {modal === '__new__' && (
              <div className="form-group">
                <label className="form-label">Job Name</label>
                <input className="form-input" placeholder="e.g. Smith Kitchen Remodel" value={formJobName} onChange={e => setFormJobName(e.target.value)} />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">Total Budget ($)</label>
              <input className="form-input" type="number" placeholder="0.00" inputMode="decimal"
                value={formBudget} onChange={e => setFormBudget(e.target.value)}
                style={{ fontFamily: 'var(--font-mono)', fontSize: 22 }} />
            </div>

            <div className="form-group">
              <label className="form-label">Alert me when I reach (%) of budget</label>
              <input className="form-input" type="number" min="1" max="99" value={formThreshold}
                onChange={e => setFormThreshold(e.target.value)} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn--secondary" onClick={() => setModal(null)} style={{ flex: 1 }}>Cancel</button>
              <button className="btn btn--primary" onClick={saveBudget} style={{ flex: 1 }}>Save Budget</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
